import { router } from "@inertiajs/react"
import { useEffect, useRef } from "react"

const DEFAULT_INTERVAL_MS = 4000

// How much longer than the request's own window to keep looking. A request
// nobody answered is only flipped to expired when the server next reads it, so
// stopping exactly at the deadline would leave the diner on "waiting for
// approval" forever — the bug this hook exists to close, reintroduced at the
// last second. Two intervals is enough to land that read.
const GRACE_TICKS = 2

// Consecutive asks that never came back successfully. Past this the endpoint is
// down or the phone is off the network, and hammering it for the rest of the
// window helps nobody.
const MAX_FAILURES = 3

type UseEntryRequestWatchOptions = {
  enabled: boolean
  /**
   * The invite's canonical path. Deliberately explicit rather than reloading
   * whatever URL the browser is on: a refused geofence check leaves the address
   * at the POST-only presence endpoint, and reloading that is a 404.
   */
  path: string
  /**
   * How long the request has left, from the server. A duration rather than a
   * timestamp because it is measured against the phone's clock: an absolute
   * `expires_at` would make a device running a minute fast stop watching a
   * minute early, and a diner approved inside that minute would never find out.
   */
  expiresInSeconds: number | null
  intervalMs?: number
}

/**
 * Watches a pending entry request until somebody answers it.
 *
 * The diner waiting for approval has no session and no user — that is phase 2's
 * whole point, and it rules out both the notification stream (built around
 * `Current.user`) and the location channel (subscribing an anonymous holder of a
 * printed QR to a location's events would hand them the restaurant's traffic).
 * What is left is asking the invite again, which already seats an approved diner
 * on its own, bounded by the request's own window.
 */
export function useEntryRequestWatch({
  enabled,
  path,
  expiresInSeconds,
  intervalMs = DEFAULT_INTERVAL_MS,
}: UseEntryRequestWatchOptions) {
  // Refs, not effect-local variables. Every successful ask changes
  // `expiresInSeconds`, which re-runs the effect — so anything holding a count
  // across asks has to outlive it, or the throttle and the failure ceiling reset
  // themselves on exactly the ticks they exist to bound.
  const askedAtRef = useRef(0)
  const inFlightRef = useRef(false)
  const failuresRef = useRef(0)

  useEffect(() => {
    if (!enabled) return undefined

    // Both ends of this comparison come from the same clock, so however wrong
    // that clock is, it is wrong consistently.
    const windowMs = expiresInSeconds === null ? null : expiresInSeconds * 1000
    const deadline =
      windowMs === null ? Number.POSITIVE_INFINITY : Date.now() + windowMs + intervalMs * GRACE_TICKS

    let timer: number | null = null

    const clear = () => {
      if (timer === null) return

      window.clearTimeout(timer)
      timer = null
    }

    const ask = () => {
      // One at a time. Without this a slow network turns every tick into a visit
      // that cancels the one before it, and none of them ever lands.
      if (inFlightRef.current) return

      inFlightRef.current = true
      askedAtRef.current = Date.now()
      let succeeded = false

      router.visit(path, {
        preserveScroll: true,
        preserveState: true,
        replace: true,
        onSuccess: () => {
          succeeded = true
        },
        onFinish: () => {
          inFlightRef.current = false
          failuresRef.current = succeeded ? 0 : failuresRef.current + 1
        },
      })
    }

    const schedule = (delay = intervalMs) => {
      clear()
      if (Date.now() > deadline) return
      if (failuresRef.current >= MAX_FAILURES) return

      timer = window.setTimeout(() => {
        // A phone in a pocket is not a diner reading a screen. Asking anyway
        // would spend the window on answers nobody sees.
        if (!document.hidden) ask()

        schedule()
      }, delay)
    }

    // Taking the phone back out is exactly the moment the answer matters, so it
    // does not wait for the next tick — but it still respects the interval, or a
    // diner flicking between tabs would ask as fast as they can switch.
    const onVisibilityChange = () => {
      if (document.hidden || Date.now() > deadline) return

      const sinceLastAsk = Date.now() - askedAtRef.current
      if (sinceLastAsk >= intervalMs) {
        ask()
        schedule()
        return
      }

      schedule(intervalMs - sinceLastAsk)
    }

    document.addEventListener("visibilitychange", onVisibilityChange)
    schedule()

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      clear()
    }
  }, [enabled, path, expiresInSeconds, intervalMs])
}
